import React, {
  useMemo, useState, useEffect, useRef, useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { WindRose } from 'src/components/ApartmentPageV2/WindRose';
import { WindRoseProvider } from 'src/components/ApartmentPageV2/WindRose/context';
import { randomHash } from 'src/helpers';
import { useAppVisibilitySpinnerBtn } from 'src/hooks/app';
import { PopupProvider } from 'src/components/ApartmentPageV2/ViewSpinner/context';
import { AppPopover } from 'src/components/ApartmentPageV2/AppPopover';
import { ListOfKeysFilterAllData } from 'src/typings/filter';
import { requestSelectors } from 'src/store/requests/selectors';
import { useGetFilteredIds } from 'src/components/ApartmentPageV2/ViewSpinner/components/RenderSvg/hooks';
import { useBuildType } from 'src/hooks';
import { usePolygonConfig } from 'src/components/ViewSpinner/components/RenderSvg/hooks';
import { getSpinnerDetailInfo } from 'src/store/requests/spinner-views/selector';
import { ISpinnerFitType } from 'src/typings/spinner';
import {
  ArrowBack, ArrowForward, ZoomIn, ZoomOut,
} from '@mui/icons-material';
import { useAppMediaQuery } from 'src/hooks/app/useAppMediaQuery';
import { useStyles } from './styles';
import { useInitSpinner } from './hooks';
import { IAppSpinner } from './types';

const AppSpinnerComponent: React.FC<IAppSpinner> = React.memo(({
  customDisplayIds,
  hideControls,
  spinnerData,
  roseInTop,
  type,
  spinnerHeight,
  hideWindRose = false,
  hideFloor = false,
  objectFit = ISpinnerFitType.cover,
  wrapperClassName = '',
  onWindRoseOnload,
  onClick,
}) => {
  const randomSpinnerId: string = useMemo(randomHash, []);
  const { is } = useAppMediaQuery();
  const isNotLarge = is('(max-width:1280px)');
  const styles = useStyles({});
  const spinnerRef = useRef<HTMLDivElement>();
  const { globalPolygonConfig } = usePolygonConfig();
  const detailInfo = useSelector(getSpinnerDetailInfo);
  const { isCrossTable } = useBuildType();
  const { filteredIds } = useGetFilteredIds();
  const { gallerySpinnerBtnViews } = useAppVisibilitySpinnerBtn();
  const filteredIdsRef = useRef(customDisplayIds || filteredIds);
  filteredIdsRef.current = customDisplayIds || filteredIds as string[];
  const getPopupConfig = useSelector(requestSelectors.app.getPopupConfig);
  const [hideSpinner, setHideSpinner] = useState<boolean>(false);

  const {
    listOfStops,
    spinner,
  } = useInitSpinner(
    spinnerData,
    randomSpinnerId,
    hideWindRose,
    type,
    objectFit,
    spinnerRef,
    appendChildHandler,
    filteredIdsRef.current,
  );

  const getStaticBodyColor = useCallback((nameId: string) => {
    return ((getPopupConfig?.enable_polygon_status_colors || isCrossTable)
      && detailInfo?.placement?.[nameId]) ? detailInfo?.placement[nameId][isCrossTable ? (
        ListOfKeysFilterAllData.placementStatusCrossTableColor
      ) : ListOfKeysFilterAllData.placementStatusColor] : `${globalPolygonConfig?.static_body_color}`;
  }, [getPopupConfig, isCrossTable, detailInfo, globalPolygonConfig]);

  useEffect(() => {
    spinner?.children.forEach((item: any) => {
      if ((filteredIdsRef.current as string[])?.includes(item.userData['entity-view-name-id'])) {
        item.domElement.setAttribute('stroke', globalPolygonConfig?.static_border_color);
        item.domElement.setAttribute('fill', getStaticBodyColor(item.userData['entity-view-name-id']));
        item.domElement.setAttribute('fill-opacity', globalPolygonConfig?.static_body_opacity);
      } else {
        item.domElement.setAttribute('stroke', 'transparent');
        item.domElement.setAttribute('fill', 'transparent');
        item.domElement.setAttribute('fill-opacity', '0');
      }
    });
  }, [spinner?.children, filteredIdsRef.current, globalPolygonConfig]);

  const isHighlight = (name: string, nameId: string) => {
    // @ts-ignore
    return Array.isArray(filteredIdsRef.current) ? filteredIdsRef.current.includes(nameId) : filteredIdsRef.current[name]?.includes(nameId);
  };

  function appendChildHandler({ child }: {target: any, child: any}) {
    const { domElement, type: childType } = child;

    const {
      'hover-text': hoverText,
      infrastructure,
      'infrastructure-view-type': infrastructureViewType,
      'entity-view-id': entityViewId,
      'entity-view': entityView,
      'entity-view-name': entityViewName,
      'entity-view-type': entityViewType,
      'entity-view-name-id': entityViewNameId,
      'entity-view-stop': entityViewStop,
      'data-is-terrace': dataIsTerrace,
      'counter-id': counterId,
      'place-counter': placeCounter,
      redirect,
    } = child.userData;

    if (childType === 'area') {
      domElement.setAttribute('cursor', isHighlight(entityViewName, entityViewNameId) ? 'pointer' : 'default');
      domElement.setAttribute('stroke', isHighlight(entityViewName, entityViewNameId) ? globalPolygonConfig?.static_border_color : 'transparent');
      domElement.setAttribute(
        'fill', isHighlight(entityViewName, entityViewNameId) ? getStaticBodyColor(entityViewNameId) : 'transparent',
      );
      domElement.setAttribute(
        'fill-opacity',
        globalPolygonConfig?.static_body_opacity,
      );
    }
    child.emit('update-user-data');
  }

  const onWindRoseClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (onClick) {
      onClick(event);
    }
  };

  useEffect(() => {
    if (!hideSpinner) {
      setHideSpinner(false);
    }
  }, [filteredIdsRef]);

  return (
    <PopupProvider>

      <Box
        className={`${wrapperClassName}`}
        width="100%"
        height="100%"
        position="relative"
        alignItems="center"
        flexDirection="column"
        display={isNotLarge ? 'flex' : 'block'}
      >
        {!hideWindRose && (
          <>
            <Box className="holder" onClick={(event) => onWindRoseClick(event)}>
              <WindRose
                randomSpinnerId={randomSpinnerId}
                onTop={roseInTop}
                onLoad={onWindRoseOnload}
              />
            </Box>
          </>
        )}

        <Box
          hidden={hideFloor}
          id={`spinner_place-v2${randomSpinnerId}`}
          className="spinner_place-v2"
          height={spinnerHeight ? `${spinnerHeight}px !important` : 'dontknow'}
          data-appspinner-id={randomSpinnerId}
        >
          <Box
            id={`spinner_app_canvas${randomSpinnerId}`}
            className={`spinner_app_canvas ${styles.canvasStyle}`}
            ref={spinnerRef}
          />
          {!hideFloor && (
            <>
              { !hideControls && (
              <div id={`spinner_control-v2${randomSpinnerId}`} className={`spinner_control-v2 ${gallerySpinnerBtnViews}`}>
                <div
                  className={`turn_left ${listOfStops.length <= 1 ? styles.hideRotate : ''} ${styles.turnBtn}`}
                  id={`turn_left${randomSpinnerId}`}
                >
                  <ArrowBack className="arrow_back" />
                </div>
                <div
                  className={`turn_right ${listOfStops.length <= 1 ? styles.hideRotate : ''} ${styles.turnBtn}`}
                  id={`turn_right${randomSpinnerId}`}
                >
                  <ArrowForward className="arrow_forward" />
                </div>
                <div className="spinner_zoom" id={`spinner_zoom${randomSpinnerId}`}><ZoomIn /></div>
                <div className="spinner_unzoom" id={`spinner_unzoom${randomSpinnerId}`}><ZoomOut /></div>
              </div>
              )}

              <div id={`porgress_bar${randomSpinnerId}`} className="porgress_bar" />
            </>
          )}
        </Box>
      </Box>
      <AppPopover />
    </PopupProvider>
  );
});

export const AppSpinner: React.FC<IAppSpinner> = (props) => {
  return (
    <WindRoseProvider>
      <AppSpinnerComponent {...props} />
    </WindRoseProvider>
  );
};
